import { useState, useCallback } from 'react'
import { useStore } from 'react-redux'
import { surveyService } from '../services/surveyService'

/**
 * Custom hook for survey mapping form operations
 * Handles CRUD operations, dialog state, and success/error management
 * 
 * @param {object} options - Configuration options
 * @param {Function} options.onSaveSuccess - Called after successful save
 * @param {Function} options.onDeleteSuccess - Called after successful delete
 * @param {Function} options.onStatusChangeSuccess - Called after successful status change
 */
export const useSurveyMappingForm = (options = {}) => {
  const store = useStore()

  const [dialog, setDialog] = useState({
    open: false,
    dialogActions: [],
  })
  const [clear, setClear] = useState(true)
  const [ready, setReady] = useState(false)
  const [saving, setSaving] = useState(false)

  /**
   * Show a dialog with custom configuration
   */
  const showDialog = useCallback((dialogConfig) => {
    setDialog({
      open: true,
      ...dialogConfig,
    })
  }, [])

  /**
   * Close the current dialog
   */
  const closeDialog = useCallback(() => {
    setDialog({
      open: false,
      dialogActions: [],
    })
  }, [])

  /**
   * Mark the form as "dirty" (not cleared)
   */
  const markDirty = useCallback(() => {
    setClear(false)
  }, [])

  /**
   * Reset the form to initial state
   */
  const resetForm = useCallback(() => {
    setClear(true)
    setReady(false)
    closeDialog()
  }, [closeDialog])

  /**
   * Create a new survey mapping
   */
  const createMapping = useCallback(async (data) => {
    setSaving(true)
    try {
      await surveyService.createMapping(data, store)
      options.onSaveSuccess?.()
      markDirty()
      return true
    } catch (error) {
      console.error('Failed to create survey mapping:', error)
      return false
    } finally {
      setSaving(false)
    }
  }, [store, options, markDirty])

  /**
   * Update an existing survey mapping
   */
  const updateMapping = useCallback(async (id, data) => {
    setSaving(true)
    try {
      await surveyService.updateMapping(id, data, store)
      options.onSaveSuccess?.()
      markDirty()
      return true
    } catch (error) {
      console.error('Failed to update survey mapping:', error)
      return false
    } finally {
      setSaving(false)
    }
  }, [store, options, markDirty])

  /**
   * Delete a survey mapping (soft delete by setting status to 'D')
   */
  const deleteMapping = useCallback(async (id) => {
    setSaving(true)
    try {
      await surveyService.updateMappingStatus(id, 'D', store)
      options.onDeleteSuccess?.()
      markDirty()
      return true
    } catch (error) {
      console.error('Failed to delete survey mapping:', error)
      return false
    } finally {
      setSaving(false)
    }
  }, [store, options, markDirty])

  /**
   * Toggle mapping status between Active ('A') and Paused ('P')
   */
  const toggleMappingStatus = useCallback(async (id, currentStatus) => {
    setSaving(true)
    try {
      const newStatus = currentStatus === 'P' ? 'A' : 'P'
      await surveyService.updateMappingStatus(id, newStatus, store)
      options.onStatusChangeSuccess?.()
      markDirty()
      return true
    } catch (error) {
      console.error('Failed to toggle survey mapping status:', error)
      return false
    } finally {
      setSaving(false)
    }
  }, [store, options, markDirty])

  return {
    // State
    dialog,
    clear,
    ready,
    saving,

    // Setters
    setClear,
    setReady,

    // Dialog actions
    showDialog,
    closeDialog,

    // Form actions
    resetForm,
    markDirty,

    // CRUD operations
    createMapping,
    updateMapping,
    deleteMapping,
    toggleMappingStatus,
  }
}
